/**
 * SSOT for operation-request order picker (and similar): map raw + normalized row → correct ₪ / $ labels.
 * Display-only — does not change billing amounts.
 */

import { parseNumber } from "../services/currencyService";
import { extractLdsOrderFinalPriceIls } from "./extractLdsOrderFinalPriceIls";

const LDS_LIKE_CATEGORIES = new Set(["חבילות", "טיסות", "טיולים מאורגנים", "חבילות דינמיות"]);

function num(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}

export function roughlyEqual(a, b, rel = 1e-6, abs = 0.5) {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) <= Math.max(abs, Math.abs(a) * rel);
}

function upperCcy(v) {
  return String(v ?? "")
    .trim()
    .toUpperCase();
}

function parsePackageDetailsObject(order) {
  let pd = order?.packagepricedetails ?? order?.packagePriceDetails;
  if (typeof pd === "string") {
    try {
      pd = JSON.parse(pd);
    } catch {
      pd = null;
    }
  }
  return pd && typeof pd === "object" ? pd : null;
}

function hotelLikeCategory(category) {
  const c = String(category || "");
  return c === "מלונות" || c.includes("מלון");
}

export function extractAuthoritativeUsdTotal(raw) {
  if (!raw || typeof raw !== "object") return { usd: null, source: null };
  const top = num(raw.total_usd ?? raw.totalUsd ?? raw.TotalUsd);
  if (top != null && top > 0) return { usd: top, source: "total_usd" };

  const pd = parsePackageDetailsObject(raw);
  const ccy = upperCcy(pd?.Currency);
  const fp = num(pd?.FinalPrice);
  if (ccy === "USD" && fp != null && fp > 0) return { usd: fp, source: "packagepricedetails.FinalPrice+USD" };

  const sup = upperCcy(pd?.SupplierCurrency);
  const fps = num(pd?.FinalPriceInSupplierCurrency);
  if (sup === "USD" && fps != null && fps > 0) return { usd: fps, source: "packagepricedetails.FinalPriceInSupplierCurrency+USD" };

  return { usd: null, source: null };
}

function extractExplicitIlsTotal(raw) {
  if (!raw || typeof raw !== "object") return { ils: null, source: null };
  const keys = [
    ["canonical_total_price_ils", raw.canonical_total_price_ils ?? raw.canonicalTotalPriceIls],
    ["display_total_ils", raw.display_total_ils],
    ["final_total_ils", raw.final_total_ils],
    ["total_ils", raw.total_ils ?? raw.totalIls],
    ["customer_total", raw.customer_total],
  ];
  for (const [name, v] of keys) {
    const n = num(v);
    if (n != null && n > 0) return { ils: n, source: name };
  }
  return { ils: null, source: null };
}

function extractLdsCanonicalTotalIls(raw) {
  const extracted = extractLdsOrderFinalPriceIls(raw);
  const n = num(extracted);
  return n != null && n > 0 ? n : null;
}

function extractExplicitRemainingIls(raw) {
  if (!raw || typeof raw !== "object") return null;
  return (
    num(raw.remaining_ils ?? raw.remainingIls ?? raw.balance_ils ?? raw.balanceIls ?? raw.remaining_balance_ils) ??
    null
  );
}

function extractExplicitRemainingUsd(raw) {
  if (!raw || typeof raw !== "object") return null;
  return num(raw.remaining_usd ?? raw.remainingUsd ?? raw.balance_usd) ?? null;
}

function extractExplicitPaidIls(raw) {
  if (!raw || typeof raw !== "object") return null;
  return (
    num(
      raw.sum_paid_ils ??
        raw.sumPaidIls ??
        raw.paid_ils ??
        raw.paidIls ??
        raw.total_paid_ils,
    ) ?? null
  );
}

function extractExplicitPaidUsd(raw) {
  if (!raw || typeof raw !== "object") return null;
  return (
    num(
      raw.sumPaidUsd ??
        raw.sum_paid_usd ??
        raw.sumpaid_usd ??
        raw.paid_usd ??
        raw.paidUsd,
    ) ?? null
  );
}

export function formatDualCurrencyLine(ils, usd) {
  const parts = [];
  if (ils != null && Number.isFinite(ils)) parts.push(`${parseNumber(ils)} ₪`);
  if (usd != null && Number.isFinite(usd)) parts.push(`${parseNumber(usd)}$`);
  if (!parts.length) return "—";
  return parts.join(" / ");
}

function isLdsLikeRow(pickerRow, raw, category) {
  if (LDS_LIKE_CATEGORIES.has(category)) return true;
  if (raw.dealid != null || raw.dealtype != null) return true;
  if (raw.ourprice && typeof raw.ourprice === "object") return true;
  if (raw.ourPrice && typeof raw.ourPrice === "object") return true;
  return false;
}

/**
 * @typedef {{ ils: number|null, usd: number|null, currencySource: string }} AmountFacet
 * @typedef {{
 *   total: AmountFacet,
 *   remaining: AmountFacet,
 *   displayTotalText: string,
 *   displayRemainingText: string,
 *   displayPaidText: string,
 *   trace: Record<string, unknown>,
 * }} OrderAmountDisplayResolution
 */

/**
 * Resolve correct ₪ / $ labels for a picker row (normalized order + raw API object).
 * @param {Record<string, unknown>|null|undefined} pickerRow
 * @param {{ emitTrace?: boolean }} [options]
 * @returns {OrderAmountDisplayResolution}
 */
export function resolveOrderAmountDisplayForPicker(pickerRow, options = {}) {
  const emitTrace = options.emitTrace === true;

  const raw =
    pickerRow?.raw && typeof pickerRow.raw === "object" && !Array.isArray(pickerRow.raw)
      ? pickerRow.raw
      : {};

  const normTotal = num(pickerRow?.totalAmount);
  const normPaid = num(pickerRow?.paidAmount);
  const normRem = num(pickerRow?.remainingAmount);

  const declaredRow = upperCcy(pickerRow?.currency);
  const declaredRaw = upperCcy(raw.currency ?? raw.Currency);
  const declared = declaredRow || declaredRaw || "ILS";

  const category = pickerRow?.category != null ? String(pickerRow.category) : "";
  const sourceType = category || (pickerRow?.rawOrderType != null ? String(pickerRow.rawOrderType) : "unknown");

  const explicitIls = extractExplicitIlsTotal(raw);
  const ldsCanonIls = extractLdsCanonicalTotalIls(raw);
  const ilsEvidence = explicitIls.ils ?? ldsCanonIls;
  const { usd: authoritativeUsd, source: usdFieldSource } = extractAuthoritativeUsdTotal(raw);

  const pwccf = num(raw.ourprice?.priceWithCreditCardFee ?? raw.ourPrice?.priceWithCreditCardFee);

  const ldsLike = isLdsLikeRow(pickerRow, raw, category);

  let totalIls = null;
  let totalUsd = null;
  let totalCurrencySource = "normalized_row";

  if (hotelLikeCategory(category)) {
    if (declared === "USD" && authoritativeUsd != null && normTotal != null && roughlyEqual(authoritativeUsd, normTotal)) {
      totalUsd = normTotal;
      totalIls = explicitIls.ils;
      totalCurrencySource = usdFieldSource || "hotel_usd_package_price";
    } else if (declared === "USD") {
      totalUsd = normTotal;
      totalIls = explicitIls.ils;
      totalCurrencySource = "hotel_declared_usd";
    } else {
      totalIls = normTotal;
      totalUsd = authoritativeUsd != null && !roughlyEqual(authoritativeUsd, normTotal ?? NaN) ? authoritativeUsd : null;
      if (totalUsd != null) totalCurrencySource = `${usdFieldSource || "parallel_usd"}_hotel`;
      else totalCurrencySource = "hotel_ils";
    }
  } else if (ldsLike) {
    const ilsAligned =
      normTotal != null &&
      ilsEvidence != null &&
      roughlyEqual(ilsEvidence, normTotal) &&
      (pwccf == null || roughlyEqual(pwccf, normTotal));

    const misdeclaredUsd =
      declared === "USD" && authoritativeUsd == null && ilsAligned;

    if (misdeclaredUsd) {
      totalIls = normTotal;
      totalUsd = null;
      totalCurrencySource = "lds_fix_currency_field_vs_pwccf_il";
    } else if (
      explicitIls.ils != null &&
      authoritativeUsd != null &&
      normTotal != null &&
      roughlyEqual(explicitIls.ils, normTotal) &&
      !roughlyEqual(authoritativeUsd, normTotal)
    ) {
      totalIls = normTotal;
      totalUsd = authoritativeUsd;
      totalCurrencySource = "dual_explicit_ils_total_plus_authoritative_usd";
    } else if (authoritativeUsd != null && normTotal != null && roughlyEqual(authoritativeUsd, normTotal)) {
      totalUsd = normTotal;
      totalIls = explicitIls.ils;
      totalCurrencySource = usdFieldSource || "authoritative_usd_matches_norm";
    } else if (declared === "USD" && authoritativeUsd != null) {
      totalUsd = normTotal;
      totalIls = explicitIls.ils;
      totalCurrencySource = usdFieldSource || "declared_usd_with_field";
    } else {
      totalIls = normTotal;
      totalUsd =
        authoritativeUsd != null && normTotal != null && !roughlyEqual(authoritativeUsd, normTotal)
          ? authoritativeUsd
          : null;
      totalCurrencySource =
        totalUsd != null ? `${usdFieldSource || "parallel_usd"}_with_ils_norm` : "lds_norm_as_ils";
    }
  } else {
    if (declared === "USD" && authoritativeUsd != null && normTotal != null && roughlyEqual(authoritativeUsd, normTotal)) {
      totalUsd = normTotal;
      totalIls = explicitIls.ils;
      totalCurrencySource = usdFieldSource || "generic_usd";
    } else if (declared === "USD") {
      totalUsd = normTotal;
      totalIls = explicitIls.ils;
      totalCurrencySource = "generic_declared_usd";
    } else {
      totalIls = normTotal;
      totalUsd =
        authoritativeUsd != null && normTotal != null && !roughlyEqual(authoritativeUsd, normTotal)
          ? authoritativeUsd
          : null;
      totalCurrencySource = totalUsd != null ? `generic_${usdFieldSource || "usd_extra"}` : "generic_ils";
    }
  }

  if (normTotal == null && totalIls == null && totalUsd == null) {
    const emptyTrace = {
      orderId: pickerRow?.orderId ?? pickerRow?.id ?? null,
      displayOrderNumber: pickerRow?.displayOrderNumber ?? null,
      sourceType,
      rawTotal: pickerRow?.totalAmount ?? null,
      rawTotalCurrency: pickerRow?.currency ?? raw.currency ?? null,
      totalIls: null,
      totalUsd: null,
      remainingIls: null,
      remainingUsd: null,
      selectedDisplayAmount: null,
      selectedDisplayCurrency: null,
      rawOrder: raw,
    };
    return {
      total: { ils: null, usd: null, currencySource: "none" },
      remaining: { ils: null, usd: null, currencySource: "none" },
      displayTotalText: "—",
      displayRemainingText: "—",
      displayPaidText: "—",
      trace: emptyTrace,
    };
  }

  /** @type {AmountFacet} */
  const total = {
    ils: totalIls,
    usd: totalUsd,
    currencySource: totalCurrencySource,
  };

  let paidIls = extractExplicitPaidIls(raw);
  let paidUsd = extractExplicitPaidUsd(raw);
  let remIls = extractExplicitRemainingIls(raw);
  let remUsd = extractExplicitRemainingUsd(raw);

  if (paidIls == null && paidUsd == null && normPaid != null) {
    if (totalUsd != null && totalIls != null) {
      paidIls = normPaid;
    } else if (totalUsd != null && totalIls == null) {
      paidUsd = normPaid;
    } else {
      paidIls = normPaid;
    }
  }
  if (remIls == null && remUsd == null && normRem != null) {
    if (totalUsd != null && totalIls != null) {
      remIls = normRem;
    } else if (totalUsd != null && totalIls == null) {
      remUsd = normRem;
    } else {
      remIls = normRem;
    }
  }

  const remaining = {
    ils: remIls,
    usd: remUsd,
    currencySource: totalCurrencySource,
  };

  const displayTotalText = formatDualCurrencyLine(totalIls, totalUsd);
  const displayPaidText = formatDualCurrencyLine(paidIls, paidUsd);
  const displayRemainingText = formatDualCurrencyLine(remIls, remUsd);

  let selectedDisplayAmount = normTotal;
  let selectedDisplayCurrency = declared;
  if (totalIls != null && totalUsd == null) {
    selectedDisplayCurrency = "ILS";
    selectedDisplayAmount = totalIls;
  } else if (totalUsd != null && totalIls == null) {
    selectedDisplayCurrency = "USD";
    selectedDisplayAmount = totalUsd;
  } else if (totalIls != null) {
    selectedDisplayCurrency = "BOTH";
    selectedDisplayAmount = totalIls;
  }

  const trace = {
    orderId: pickerRow?.orderId ?? pickerRow?.id ?? null,
    displayOrderNumber: pickerRow?.displayOrderNumber ?? null,
    sourceType,
    rawTotal: pickerRow?.totalAmount ?? null,
    rawTotalCurrency: pickerRow?.currency ?? raw.currency ?? null,
    totalIls,
    totalUsd,
    remainingIls: remIls,
    remainingUsd: remUsd,
    selectedDisplayAmount,
    selectedDisplayCurrency,
    rawOrder: raw,
  };

  if (emitTrace) {
    console.log("[OP_REQUEST_ORDER_PICKER_AMOUNT_TRACE]", trace);
  }

  return {
    total,
    remaining,
    displayTotalText,
    displayRemainingText,
    displayPaidText,
    trace,
  };
}
